import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useThemeStore } from "@/stores/theme";
import api from "@/helpers/apiClient";
import { trackPageView } from "@/helpers/analytics";

// Layouts
//
// Lazy igual que las páginas. Estático, este import metía AppLayout y todo su
// árbol (SidebarNavigation, Breadcrumb, CommandPalette, headlessui) en el chunk
// inicial: lo descargaba también quien caía en la landing desde un anuncio y no
// se logueaba nunca.
const AppLayout = () => import("@/layouts/AppLayout.vue");

// Pages - Lazy loading for better performance
const LandingPage = () => import("@/pages/public/LandingPage.vue");
const LoginPage = () => import("@/pages/auth/LoginPage.vue");
// El alta por defecto sigue siendo manual (el visitante la pide por WhatsApp
// desde la landing). Esta ruta existe para pasarla por privado a quien uno
// quiera, y solo funciona si el backend tiene PERMITIR_REGISTROS=true.
// No está enlazada desde ningún lado: no hay botón de "Crear cuenta".
const SignUpPage = () => import("@/pages/auth/SignUpPage.vue");
const OnboardingPage = () => import("@/pages/auth/OnboardingPage.vue");
const HomePage = () => import("@/pages/dashboard/HomePage.vue");
const CalendarPage = () => import("@/pages/calendario/CalendarPage.vue");
const NewVentaPage = () => import("@/pages/ventas/NewVentaPage.vue");
const VentasPage = () => import("@/pages/ventas/VentasPage.vue");
const VentaDetallePage = () => import("@/pages/ventas/VentaDetallePage.vue");
const NewGastoPage = () => import("@/pages/gastos/NewGastoPage.vue");
const GastosPage = () => import("@/pages/gastos/GastosPage.vue");
const NewPresupuestoPage = () => import("@/pages/presupuestos/NewPresupuestoPage.vue");
const NewProductoPage = () => import("@/pages/productos/NewProductoPage.vue");
const PresupuestosPage = () => import("@/pages/presupuestos/PresupuestosPage.vue");
const PresupuestoDetallePage = () => import("@/pages/presupuestos/PresupuestoDetallePage.vue");
const ProductosPage = () => import("@/pages/productos/ProductosPage.vue");
const ProductosAnalyticsPage = () => import("@/pages/productos/ProductosAnalyticsPage.vue");
const CatalogoPage = () => import("@/pages/catalogo/CatalogoPage.vue");
const ClientesPage = () => import("@/pages/clientes/ClientesPage.vue");
const ClienteDetallePage = () => import("@/pages/clientes/ClienteDetallePage.vue");
const ReportesPage = () => import("@/pages/reportes/ReportesPage.vue");
const AsistentePage = () => import("@/pages/asistente/AsistentePage.vue");
const ProveedoresPage = () => import("@/pages/proveedores/ProveedoresPage.vue");
const ProveedorDetallePage = () => import("@/pages/proveedores/ProveedorDetallePage.vue");
const PedidosPage = () => import("@/pages/pedidos/PedidosPage.vue");
const NewPedidoPage = () => import("@/pages/pedidos/NewPedidoPage.vue");
const PedidoDetallePage = () => import("@/pages/pedidos/PedidoDetallePage.vue");
const UsuariosPage = () => import("@/pages/sistema/UsuariosPage.vue");
const ConfiguracionPage = () => import("@/pages/sistema/ConfiguracionPage.vue");
const PerfilPage = () => import("@/pages/sistema/PerfilPage.vue");
const SoportePage = () => import("@/pages/sistema/SoportePage.vue");
const EscanearPage = () => import("@/pages/sistema/EscanearPage.vue");
const SuperAdminPage = () => import("@/pages/admin/SuperAdminPage.vue");
const ShareTestPage = () => import("@/pages/public/ShareTestPage.vue");
const TrackingPublicoPage = () => import("@/pages/public/TrackingPublicoPage.vue");
const PrivacyPolicyPage = () => import("@/pages/public/PrivacyPolicyPage.vue");
const TermsConditionsPage = () => import("@/pages/public/TermsConditionsPage.vue");

const SITE_NAME = "Olitom";
const INDEX_ROBOTS_DIRECTIVE =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const NOINDEX_ROBOTS_DIRECTIVE = "noindex, nofollow, noarchive";

const DEFAULT_SEO = {
  title: "Sistema de gestion para emprendimientos en Argentina | Olitom",
  description:
    "Sistema de gestion para emprendimientos en Argentina: presupuestos, ventas, cuenta corriente, control de gastos, inteligencia artificial y estadisticas inteligentes en una sola plataforma.",
  keywords:
    "sistema de gestion, emprendimientos argentina, presupuestos, ventas, cuenta corriente, inteligencia artificial, estadisticas inteligentes, software de gestion, pymes",
  image: "/img/icons/favicon.png",
};

const ROUTE_SEO = {
  Landing: {
    title: "Sistema de gestion para emprendimientos en Argentina | Olitom",
    description:
      "Olitom te ayuda a profesionalizar tu emprendimiento con presupuestos, ventas, cuenta corriente, inteligencia artificial y estadisticas inteligentes.",
    canonicalPath: "/",
    robots: INDEX_ROBOTS_DIRECTIVE,
    ogType: "website",
    keywords:
      "sistema de gestion para emprendimientos, software de gestion argentina, presupuestos y ventas, cuenta corriente clientes, inteligencia artificial para negocios",
  },
  Login: {
    title: "Iniciar sesion | Olitom",
    description: "Accede a tu sistema de gestion Olitom.",
    canonicalPath: "/login",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  SignUp: {
    title: "Crear cuenta | Olitom",
    description: "Alta de cuenta en Olitom.",
    canonicalPath: "/registro",
    // Sin esto, Google indexaría la URL que estás pasando a mano y dejaría de
    // ser privada: aparecería buscando "olitom registro".
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  ShareTest: {
    title: "Prueba tecnica de compartir | Olitom",
    description: "Pagina tecnica interna de pruebas de compartido.",
    canonicalPath: "/share-test",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  TrackingPublico: {
    title: "Seguimiento de pedido | Olitom",
    description: "Consulta el estado de tu pedido en tiempo real.",
    canonicalPath: "/tracking",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  PrivacyPolicy: {
    title: "Politica de privacidad y cookies | Olitom",
    description:
      "Conoce como Olitom protege tus datos personales y como gestionamos cookies y consentimiento.",
    canonicalPath: "/politicas-de-privacidad",
    robots: INDEX_ROBOTS_DIRECTIVE,
  },
  TermsConditions: {
    title: "Terminos y condiciones de uso | Olitom",
    description:
      "Revisa los terminos y condiciones de Olitom para el uso del sistema de gestion y sus modulos de ventas, presupuestos e IA.",
    canonicalPath: "/terminos-y-condiciones",
    robots: INDEX_ROBOTS_DIRECTIVE,
  },
  Onboarding: {
    title: "Onboarding | Olitom",
    description: "Configuracion inicial de tu cuenta.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Home: {
    title: "Dashboard de gestion | Olitom",
    description: "Panel principal con resumen de tu negocio.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Calendario: {
    title: "Calendario de operaciones | Olitom",
    description: "Organiza vencimientos, tareas y eventos de tu negocio.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  "Nueva Venta": {
    title: "Registrar nueva venta | Olitom",
    description: "Crea ventas de forma rapida y profesional.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Ventas: {
    title: "Historial de ventas | Olitom",
    description: "Consulta tu historial de ventas y pagos.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  VentaDetalle: {
    title: "Detalle de venta | Olitom",
    description: "Visualiza el detalle completo de una venta.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  "Nuevo Gasto": {
    title: "Registrar gasto | Olitom",
    description: "Registra gastos y mejora el control de rentabilidad.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Gastos: {
    title: "Historial de gastos | Olitom",
    description: "Analiza gastos por categoria y periodo.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Presupuesto: {
    title: "Generar presupuesto | Olitom",
    description: "Crea presupuestos profesionales y compartibles.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Presupuestos: {
    title: "Historial de presupuestos | Olitom",
    description: "Gestiona presupuestos y conversiones a venta.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  PresupuestoDetalle: {
    title: "Detalle de presupuesto | Olitom",
    description: "Consulta el detalle y estado de un presupuesto.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  NuevoProducto: {
    title: "Nuevo producto | Olitom",
    description: "Crea productos de forma rapida y carga imagenes desde galeria o camara.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Productos: {
    title: "Gestion de productos e inventario | Olitom",
    description: "Controla stock, catalogo y movimientos de productos.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  ProductosAnalytics: {
    title: "Analitica inteligente de productos | Olitom",
    description: "Detecta productos mas vendidos, rentables y en riesgo de quiebre de stock.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Catalogo: {
    title: "Catalogo publico | Olitom",
    description: "Configura tu vitrina online y elegi que productos se publican.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Reportes: {
    title: "Estadisticas inteligentes del negocio | Olitom",
    description: "Visualiza reportes de ventas, gastos y rentabilidad con datos claros para decidir mejor.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Asistente: {
    title: "Asistente IA de tu negocio | Olitom",
    description: "Consulta tus ventas, clientes y stock, y recibe recomendaciones con inteligencia artificial.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Clientes: {
    title: "Clientes y cuenta corriente | Olitom",
    description: "Administra clientes, historial comercial y cuenta corriente.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  ClienteDetalle: {
    title: "Detalle de cliente y cuenta corriente | Olitom",
    description: "Consulta saldos, movimientos y comportamiento de compra de cada cliente.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Proveedores: {
    title: "Gestion de proveedores | Olitom",
    description: "Organiza proveedores, costos y condiciones de compra.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  ProveedorDetalle: {
    title: "Detalle de proveedor | Olitom",
    description: "Revisa historial, pedidos y datos clave de un proveedor.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Pedidos: {
    title: "Pedidos de compra | Olitom",
    description: "Gestiona pedidos de compra y seguimiento de estados.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  NewPedido: {
    title: "Nuevo pedido de compra | Olitom",
    description: "Crea pedidos a proveedores en pocos pasos.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  PedidoDetalle: {
    title: "Detalle de pedido de compra | Olitom",
    description: "Visualiza items, estados y seguimiento de un pedido.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Usuarios: {
    title: "Usuarios y permisos | Olitom",
    description: "Administra usuarios, roles y accesos del equipo.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Configuracion: {
    title: "Configuracion del sistema | Olitom",
    description: "Personaliza parametros de tu empresa y operacion.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Perfil: {
    title: "Mi perfil | Olitom",
    description: "Gestiona tus datos personales y preferencias.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Soporte: {
    title: "Soporte | Olitom",
    description: "Canal de ayuda y soporte para usuarios de Olitom.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  Escanear: {
    title: "Escáner de código de barras | Olitom",
    description: "Escanea códigos de barras con tu cámara para buscar, editar o vender productos.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
  PanelControl: {
    title: "Panel de control superadmin | Olitom",
    description: "Panel administrativo avanzado para superadmin.",
    robots: NOINDEX_ROBOTS_DIRECTIVE,
  },
};

function resolveSiteUrl() {
  const envUrl = process.env.VUE_APP_SITE_URL;
  const browserOrigin =
    typeof window !== "undefined" && window.location ? window.location.origin : "";
  const baseUrl = envUrl || browserOrigin || "";
  return baseUrl.replace(/\/+$/, "");
}

function toAbsoluteUrl(pathOrUrl = "/") {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  const siteUrl = resolveSiteUrl();
  if (!siteUrl) {
    return pathOrUrl;
  }

  const normalizedPath = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${siteUrl}${normalizedPath}`;
}

function upsertMetaTag(attribute, key, content) {
  if (!content) {
    return;
  }

  let metaTag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!metaTag) {
    metaTag = document.createElement("meta");
    metaTag.setAttribute(attribute, key);
    document.head.appendChild(metaTag);
  }

  metaTag.setAttribute("content", content);
}

function upsertCanonicalLink(href) {
  if (!href) {
    return;
  }

  let canonicalTag = document.head.querySelector('link[rel="canonical"]');
  if (!canonicalTag) {
    canonicalTag = document.createElement("link");
    canonicalTag.setAttribute("rel", "canonical");
    document.head.appendChild(canonicalTag);
  }

  canonicalTag.setAttribute("href", href);
}

function upsertAlternateLink(hreflang, href) {
  if (!hreflang || !href) {
    return;
  }

  let alternateTag = document.head.querySelector(
    `link[rel="alternate"][hreflang="${hreflang}"]`
  );

  if (!alternateTag) {
    alternateTag = document.createElement("link");
    alternateTag.setAttribute("rel", "alternate");
    alternateTag.setAttribute("hreflang", hreflang);
    document.head.appendChild(alternateTag);
  }

  alternateTag.setAttribute("href", href);
}

function getStructuredDataForRoute(routeName, canonicalUrl, description) {
  const siteUrl = toAbsoluteUrl("/");
  const logoUrl = toAbsoluteUrl(DEFAULT_SEO.image);

  if (routeName === "Landing") {
    return [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: siteUrl,
        logo: logoUrl,
        areaServed: "AR",
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Olitom - Sistema de gestion para emprendimientos",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        inLanguage: "es-AR",
        areaServed: "AR",
        url: canonicalUrl,
        offers: {
          "@type": "Offer",
          price: "15000",
          priceCurrency: "ARS",
          availability: "https://schema.org/InStock",
        },
        description,
        featureList: [
          "Presupuestos y ventas en minutos",
          "Gestion de cuenta corriente por cliente",
          "Control de gastos y rentabilidad",
          "Asistente con inteligencia artificial",
          "Estadisticas y reportes inteligentes",
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: siteUrl,
        inLanguage: "es-AR",
      },
    ];
  }

  if (routeName === "PrivacyPolicy" || routeName === "TermsConditions") {
    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: document.title,
      url: canonicalUrl,
      inLanguage: "es-AR",
      description,
    };
  }

  return null;
}

function upsertStructuredData(data) {
  let scriptTag = document.getElementById("seo-ld-json");
  if (!scriptTag) {
    scriptTag = document.createElement("script");
    scriptTag.id = "seo-ld-json";
    scriptTag.type = "application/ld+json";
    document.head.appendChild(scriptTag);
  }

  scriptTag.textContent = data ? JSON.stringify(data) : "";
}

function applyRouteSeo(to) {
  if (typeof document === "undefined") {
    return;
  }

  const routeSeo = ROUTE_SEO[to.name] || {};
  const isPrivateRoute = Boolean(to.meta.requiresAuth || to.meta.requiresSuperAdmin);

  const title =
    routeSeo.title ||
    (to.meta.title ? `${to.meta.title} | ${SITE_NAME}` : DEFAULT_SEO.title);

  const description = routeSeo.description || DEFAULT_SEO.description;
  const keywords = routeSeo.keywords || DEFAULT_SEO.keywords;
  const robots =
    routeSeo.robots || (isPrivateRoute ? NOINDEX_ROBOTS_DIRECTIVE : INDEX_ROBOTS_DIRECTIVE);
  const canonicalPath = routeSeo.canonicalPath || (isPrivateRoute ? "/login" : to.path || "/");
  const canonicalUrl = toAbsoluteUrl(canonicalPath);
  const imageUrl = toAbsoluteUrl(routeSeo.image || DEFAULT_SEO.image);
  const ogType = routeSeo.ogType || "website";

  document.documentElement.setAttribute("lang", "es-AR");
  document.title = title;

  upsertMetaTag("name", "description", description);
  upsertMetaTag("name", "keywords", keywords);
  upsertMetaTag("name", "robots", robots);
  upsertMetaTag("name", "googlebot", robots);
  upsertMetaTag("name", "author", SITE_NAME);
  upsertMetaTag("name", "language", "es-AR");
  upsertMetaTag("name", "geo.region", "AR");
  upsertMetaTag("name", "geo.placename", "Argentina");

  upsertMetaTag("property", "og:site_name", SITE_NAME);
  upsertMetaTag("property", "og:type", ogType);
  upsertMetaTag("property", "og:locale", "es_AR");
  upsertMetaTag("property", "og:title", title);
  upsertMetaTag("property", "og:description", description);
  upsertMetaTag("property", "og:url", canonicalUrl);
  upsertMetaTag("property", "og:image", imageUrl);
  upsertMetaTag(
    "property",
    "og:image:alt",
    "Olitom, sistema de gestion para emprendimientos en Argentina"
  );

  upsertMetaTag("name", "twitter:card", "summary_large_image");
  upsertMetaTag("name", "twitter:title", title);
  upsertMetaTag("name", "twitter:description", description);
  upsertMetaTag("name", "twitter:image", imageUrl);

  upsertCanonicalLink(canonicalUrl);
  upsertAlternateLink("es-AR", canonicalUrl);
  upsertAlternateLink("x-default", canonicalUrl);

  const structuredData = getStructuredDataForRoute(to.name, canonicalUrl, description);
  upsertStructuredData(structuredData);
}

const routes = [
  // Public routes (no layout)
  {
    path: "/",
    name: "Landing",
    component: LandingPage,
    meta: { requiresAuth: false, title: "Olitom - Gestion para Negocios Deportivos", category: null },
  },
  {
    path: "/login",
    name: "Login",
    component: LoginPage,
    meta: { requiresAuth: false, title: "Iniciar Sesion", category: null },
  },
  {
    path: "/registro",
    name: "SignUp",
    component: SignUpPage,
    // noindex: es una URL que se pasa a mano, no queremos que la indexen.
    meta: { requiresAuth: false, title: "Crear cuenta", category: null },
  },
  {
    path: "/share-test",
    name: "ShareTest",
    component: ShareTestPage,
    meta: { requiresAuth: false, title: "Test de Share API", category: null },
  },
  {
    path: "/tracking/:token",
    name: "TrackingPublico",
    component: TrackingPublicoPage,
    meta: { requiresAuth: false, title: "Seguimiento de Pedido", category: null },
  },
  {
    path: "/politicas-de-privacidad",
    name: "PrivacyPolicy",
    component: PrivacyPolicyPage,
    meta: { requiresAuth: false, title: "Políticas de Privacidad", category: null },
  },
  {
    path: "/terminos-y-condiciones",
    name: "TermsConditions",
    component: TermsConditionsPage,
    meta: { requiresAuth: false, title: "Términos y Condiciones", category: null },
  },

  // Onboarding (authenticated but no sidebar)
  {
    path: "/onboarding",
    name: "Onboarding",
    component: OnboardingPage,
    meta: { requiresAuth: true, title: "Bienvenido", category: null },
  },

  // Authenticated routes with AppLayout
  {
    path: "/",
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: "dashboard",
        name: "Home",
        component: HomePage,
        meta: { requiresAuth: true, title: "Inicio", category: null },
      },
      // Calendario
      {
        path: "calendario",
        name: "Calendario",
        component: CalendarPage,
        meta: { requiresAuth: true, title: "Calendario", category: "Calendario" },
      },
      // Ventas
      {
        path: "venta",
        name: "Nueva Venta",
        component: NewVentaPage,
        meta: { requiresAuth: true, title: "Nueva Venta", category: "Ventas" },
      },
      {
        path: "ventas",
        name: "Ventas",
        component: VentasPage,
        meta: { requiresAuth: true, title: "Historial", category: "Ventas" },
      },
      {
        path: "ventas/:uuid",
        name: "VentaDetalle",
        component: VentaDetallePage,
        meta: { requiresAuth: true, title: "Detalle", category: "Ventas" },
      },
      // Gastos
      {
        path: "gasto",
        name: "Nuevo Gasto",
        component: NewGastoPage,
        meta: { requiresAuth: true, title: "Nuevo Gasto", category: "Gastos" },
      },
      {
        path: "gastos",
        name: "Gastos",
        component: GastosPage,
        meta: { requiresAuth: true, title: "Historial", category: "Gastos" },
      },
      // Presupuestos
      {
        path: "presupuesto",
        name: "Presupuesto",
        component: NewPresupuestoPage,
        meta: { requiresAuth: true, title: "Generar Presupuesto", category: "Presupuestos" },
      },
      {
        path: "presupuestos",
        name: "Presupuestos",
        component: PresupuestosPage,
        meta: { requiresAuth: true, title: "Historial", category: "Presupuestos" },
      },
      {
        path: "presupuestos/:uuid",
        name: "PresupuestoDetalle",
        component: PresupuestoDetallePage,
        meta: { requiresAuth: true, title: "Detalle", category: "Presupuestos" },
      },
      // Productos
      {
        path: "producto",
        name: "NuevoProducto",
        component: NewProductoPage,
        meta: { requiresAuth: true, title: "Nuevo Producto", category: "Productos" },
      },
      {
        path: "productos",
        name: "Productos",
        component: ProductosPage,
        meta: { requiresAuth: true, title: "Ver Productos", category: "Productos" },
      },
      {
        path: "productos/analytics",
        name: "ProductosAnalytics",
        component: ProductosAnalyticsPage,
        meta: { requiresAuth: true, title: "Analytics de Productos", category: "Productos" },
      },
      // Catálogo público (la vitrina se sirve desde el proyecto olitom-tienda;
      // acá sólo se configura y se elige qué productos se publican)
      {
        path: "catalogo",
        name: "Catalogo",
        component: CatalogoPage,
        meta: { requiresAuth: true, title: "Catálogo", category: "Productos" },
      },
      // Analytics
      {
        path: "reportes",
        name: "Reportes",
        component: ReportesPage,
        meta: { requiresAuth: true, title: "Estadisticas", category: "Reportes" },
      },
      // Asistente IA
      {
        path: "asistente",
        name: "Asistente",
        component: AsistentePage,
        meta: { requiresAuth: true, title: "Asistente IA", category: "Asistente" },
      },
      // Clientes
      {
        path: "clientes",
        name: "Clientes",
        component: ClientesPage,
        meta: { requiresAuth: true, title: "Clientes", category: "Clientes" },
      },
      {
        path: "clientes/:id",
        name: "ClienteDetalle",
        component: ClienteDetallePage,
        meta: { requiresAuth: true, title: "Detalle de Cliente", category: "Clientes" },
      },
      // Proveedores & Pedidos
      {
        path: "proveedores",
        name: "Proveedores",
        component: ProveedoresPage,
        meta: { requiresAuth: true, title: "Proveedores", category: "Proveedores" },
      },
      {
        path: "proveedores/:id",
        name: "ProveedorDetalle",
        component: ProveedorDetallePage,
        meta: { requiresAuth: true, title: "Detalle de Proveedor", category: "Proveedores" },
      },
      {
        path: "pedidos",
        name: "Pedidos",
        component: PedidosPage,
        meta: { requiresAuth: true, title: "Pedidos de Compra", category: "Pedidos" },
      },
      {
        path: "pedidos/nuevo",
        name: "NewPedido",
        component: NewPedidoPage,
        meta: { requiresAuth: true, title: "Nuevo Pedido", category: "Pedidos" },
      },
      {
        path: "pedidos/:id",
        name: "PedidoDetalle",
        component: PedidoDetallePage,
        meta: { requiresAuth: true, title: "Detalle de Pedido", category: "Pedidos" },
      },
      // Sistema
      {
        path: "usuarios",
        name: "Usuarios",
        component: UsuariosPage,
        meta: { requiresAuth: true, title: "Usuarios", category: "Sistema" },
      },
      {
        path: "configuracion",
        name: "Configuracion",
        component: ConfiguracionPage,
        meta: { requiresAuth: true, title: "Configuracion", category: "Sistema" },
      },
      {
        path: "perfil",
        name: "Perfil",
        component: PerfilPage,
        meta: { requiresAuth: true, title: "Mi Perfil", category: "Sistema" },
      },
      {
        path: "soporte",
        name: "Soporte",
        component: SoportePage,
        meta: { requiresAuth: true, title: "Soporte", category: "Sistema" },
      },
      {
        path: "escanear",
        name: "Escanear",
        component: EscanearPage,
        meta: { requiresAuth: true, title: "Escáner", category: "Sistema" },
      },
      // SuperAdmin
      {
        path: "panel-control",
        name: "PanelControl",
        component: SuperAdminPage,
        meta: { requiresAuth: true, requiresSuperAdmin: true, title: "Panel de Control", category: null },
      },
    ],
  },

  // Catch-all. Sin esto una URL muerta renderiza un router-view vacío (pantalla
  // en blanco), no un 404. Importa desde que sacamos /signup: los links viejos
  // y los bookmarks caen acá y terminan en la landing, que es donde ahora se
  // pide la cuenta.
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

let authSessionValidated = false;

// Se cachea porque el valor no cambia sin reiniciar la API, y así no se pega
// un request extra cada vez que alguien entra a /registro.
let registrosPermitidos = null;
async function registrosHabilitados() {
  if (registrosPermitidos !== null) return registrosPermitidos;
  try {
    const { data } = await api.get('/api/auth-config');
    registrosPermitidos = data?.permitir_registros === true;
  } catch {
    // Ante la duda, cerrado.
    registrosPermitidos = false;
  }
  return registrosPermitidos;
}

// Guardia global para verificar autenticacion usando Pinia auth store
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  let isAuthenticated = authStore.isAuthenticated || !!authStore.accessToken;

  if (!isAuthenticated) {
    authSessionValidated = false;
  }

  if (to.meta.requiresAuth && isAuthenticated && !authSessionValidated) {
    const profileLoaded = await authStore.fetchProfile();
    if (!profileLoaded) {
      authSessionValidated = false;
      return next({ name: "Login", query: { redirect: to.fullPath } });
    }
    authSessionValidated = true;
    isAuthenticated = true;
  }

  const userRol = authStore.user?.rol;

  // Registrar si el usuario ya ha usado la app para evitar mostrarle la landing page
  if (isAuthenticated) {
    localStorage.setItem('app_used_before', 'true');
  }

  // Detectar si está instalado como PWA o si es un usuario recurrente
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
  const isReturningUser = localStorage.getItem('app_used_before') === 'true';

  // Si intenta ir a la Landing, no está autenticado, pero es un PWA o ya usó la app -> Enviar a Login
  if (to.name === "Landing" && !isAuthenticated && (isPWA || isReturningUser)) {
    return next({ name: "Login" });
  }

  // Registro: se le pregunta al backend si está habilitado. Esto es cortesía
  // para no mostrar un formulario que va a fallar; el freno de verdad lo aplica
  // better-auth con PERMITIR_REGISTROS, así que saltear este guard no sirve.
  if (to.name === "SignUp") {
    if (isAuthenticated) return next({ name: "Home" });
    if (!(await registrosHabilitados())) return next({ name: "Login" });
  }

  if (to.meta.requiresAuth && !isAuthenticated) {
    return next({ name: "Login", query: { redirect: to.fullPath } });
  } else if (to.meta.requiresSuperAdmin) {
    if (userRol !== 'superadmin') {
      return next({ name: "Home" });
    } else {
      return next();
    }
  } else if (userRol === 'superadmin' && to.meta.requiresAuth && !to.meta.requiresSuperAdmin) {
    return next({ name: "PanelControl" });
  } else if ((to.name === "Login" || to.name === "Landing") && isAuthenticated) {
    if (userRol === 'superadmin') {
      return next({ name: "PanelControl" });
    } else {
      const shouldShowOnboarding = sessionStorage.getItem('show_onboarding');
      if (shouldShowOnboarding && to.name !== "Onboarding") {
        return next({ name: "Onboarding" });
      } else {
        return next({ name: "Home" });
      }
    }
  } else if (isAuthenticated && to.name !== "Onboarding" && userRol !== 'superadmin') {
    const shouldShowOnboarding = sessionStorage.getItem('show_onboarding');
    if (shouldShowOnboarding) {
      return next({ name: "Onboarding" });
    } else {
      return next();
    }
  } else {
    return next();
  }
});

router.afterEach((to) => {
  applyRouteSeo(to);

  // La app es una SPA: sin esto GA sólo vería la primera carga y todas las
  // pantallas quedarían agrupadas bajo una sola URL. Va después de
  // `applyRouteSeo` para que document.title ya sea el de la pantalla nueva.
  trackPageView(to);

  try {
    const themeStore = useThemeStore();
    themeStore.applyThemeForRoute(to);
  } catch (e) {
    console.error('Error applying theme in router:', e);
  }
});

applyRouteSeo(router.currentRoute.value);

export default router;
